// FILE: src/pages/LandingPage.jsx
// Public marketing landing page for CashPro.
// Clean, light, friendly — white with the brand's yellow. Fully self-contained:
// all styles are scoped under `.lp-root` and explicitly neutralise the app-wide
// styles.css / bootstrap bare-element rules so nothing leaks in or out.

import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const LP_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

.lp-root {
  --yellow: #FFCE1F;
  --yellow-hi: #FFD84D;
  --yellow-deep: #FFB703;
  --amber: #F59E0B;
  --ink: #1b1408;
  --text: #2c2617;
  --muted: #7c745f;
  --paper: #ffffff;
  --cream: #FFFBF0;
  --cream-2: #FFF6DE;
  --line: #f0e8d6;
  --radius: 20px;
  --shadow-sm: 0 1px 2px rgba(60,48,12,.05), 0 6px 18px rgba(60,48,12,.06);
  --shadow-md: 0 14px 34px rgba(60,48,12,.12);
  --shadow-yellow: 0 12px 26px rgba(255,183,3,.32);
  --maxw: 1160px;

  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: var(--text); background: var(--paper); line-height: 1.55;
  -webkit-font-smoothing: antialiased; overflow-x: hidden;
}
.lp-root *, .lp-root *::before, .lp-root *::after { box-sizing: border-box; }
.lp-root h1, .lp-root h2, .lp-root h3, .lp-root h4, .lp-root p, .lp-root ul, .lp-root li { margin: 0; padding: 0; list-style: none; }
.lp-root a { color: inherit; text-decoration: none; cursor: pointer; }
.lp-root button { font-family: inherit; cursor: pointer; background: none; }
.lp-root img { max-width: 100%; display: block; }
.lp-shell { width: 100%; max-width: var(--maxw); margin: 0 auto; padding: 0 24px; }

/* ---------- Buttons ---------- */
.lp-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  font-family: inherit; font-weight: 600; font-size: 15px; border: none;
  padding: 13px 24px; border-radius: 999px; transition: transform .16s ease, box-shadow .16s ease, background .16s;
  white-space: nowrap;
}
.lp-btn-primary { background: linear-gradient(180deg, var(--yellow-hi), var(--yellow-deep)); color: #1c1500; box-shadow: var(--shadow-yellow); font-weight: 700; }
.lp-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 16px 32px rgba(255,183,3,.44); }
.lp-root .lp-btn-ghost { background: var(--paper); color: var(--ink); border: 1.6px solid var(--line); font-weight: 600; }
.lp-root .lp-btn-ghost:hover { border-color: var(--yellow-deep); transform: translateY(-2px); }
.lp-btn-ink { background: var(--ink); color: #fff; font-weight: 600; }
.lp-btn-ink:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
.lp-btn-lg { padding: 15px 30px; font-size: 16px; }

/* ---------- Nav ---------- */
.lp-nav {
  position: sticky; top: 0; z-index: 50; padding: 0; display: block; box-shadow: none;
  background: rgba(255,255,255,.82); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--line);
}
.lp-nav-inner { display: flex; align-items: center; justify-content: space-between; height: 74px; }
.lp-logo { height: 40px; width: auto; }
.lp-nav-links { display: flex; align-items: center; gap: 30px; }
.lp-nav-links a { color: #5f584a; font-size: 15px; font-weight: 500; margin: 0; transition: color .15s; }
.lp-nav-links a:hover { color: var(--ink); }
.lp-nav-cta { display: flex; align-items: center; gap: 12px; }
.lp-nav-cta .lp-signin { color: var(--ink); font-weight: 600; font-size: 15px; margin: 0; padding: 9px 8px; cursor: pointer; }
.lp-nav-cta .lp-signin:hover { color: var(--yellow-deep); }

/* ---------- Hero ---------- */
.lp-hero { position: relative; overflow: hidden; padding: 72px 0 84px; background: var(--paper); }
.lp-hero::before {
  content: ""; position: absolute; inset: 0; pointer-events: none;
  background:
    radial-gradient(620px 420px at 84% 4%, rgba(255,206,31,.20), transparent 62%),
    radial-gradient(520px 420px at 6% 96%, rgba(255,183,3,.10), transparent 60%);
}
.lp-hero-grid { position: relative; z-index: 1; display: grid; grid-template-columns: 1.05fr .95fr; gap: 56px; align-items: center; }
.lp-eyebrow {
  display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600;
  color: #8a6a00; background: var(--cream-2); border: 1px solid #f6e6b0;
  padding: 7px 15px; border-radius: 999px; margin-bottom: 22px;
}
.lp-eyebrow .lp-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--yellow-deep); }
.lp-h1 { font-size: clamp(38px, 5.3vw, 60px); font-weight: 800; letter-spacing: -0.03em; line-height: 1.05; color: var(--ink); }
.lp-mark-hl { position: relative; white-space: nowrap; z-index: 0; }
.lp-mark-hl::after {
  content: ""; position: absolute; left: -4px; right: -4px; bottom: 6%; height: 34%; z-index: -1;
  background: linear-gradient(180deg, var(--yellow-hi), var(--yellow)); border-radius: 6px; transform: rotate(-1deg);
}
.lp-sub { margin-top: 22px; font-size: clamp(16px, 1.55vw, 19px); color: var(--muted); max-width: 520px; }
.lp-hero-cta { margin-top: 32px; display: flex; gap: 14px; flex-wrap: wrap; }
.lp-chips { margin-top: 30px; display: flex; flex-wrap: wrap; gap: 10px; }
.lp-chip { display: inline-flex; align-items: center; gap: 7px; font-size: 13.5px; font-weight: 500; color: #6a6250; background: var(--cream); border: 1px solid var(--line); padding: 8px 14px; border-radius: 999px; }
.lp-chip .lp-tick { width: 18px; height: 18px; border-radius: 50%; background: var(--yellow); color: #1c1500; display: grid; place-items: center; flex: 0 0 auto; }

/* ---------- Hero mockup ---------- */
.lp-mock { position: relative; z-index: 1; animation: lp-float 7s ease-in-out infinite; }
@keyframes lp-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-13px); } }
.lp-card { background: var(--paper); border-radius: 24px; padding: 22px; box-shadow: var(--shadow-md); border: 1px solid var(--line); }
.lp-card-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.lp-card-acct { display: flex; align-items: center; gap: 12px; }
.lp-card-icon { width: 42px; height: 42px; border-radius: 13px; background: linear-gradient(150deg, var(--yellow-hi), var(--yellow-deep)); display: grid; place-items: center; color: #1c1500; }
.lp-card-acct b { font-size: 15px; font-weight: 700; color: var(--ink); display: block; }
.lp-card-acct span { font-size: 12.5px; color: var(--muted); }
.lp-badge { font-size: 12px; font-weight: 700; padding: 6px 12px; border-radius: 999px; background: var(--yellow); color: #1c1500; display: inline-flex; align-items: center; gap: 6px; }
.lp-balance { font-size: 36px; font-weight: 800; letter-spacing: -0.03em; color: var(--ink); }
.lp-balance small { font-size: 14px; color: var(--muted); font-weight: 600; margin-left: 7px; }
.lp-bars { display: flex; align-items: flex-end; gap: 7px; height: 58px; margin: 16px 0 4px; }
.lp-bars i { flex: 1; border-radius: 6px 6px 3px 3px; background: linear-gradient(180deg, var(--yellow), rgba(255,183,3,.4)); display: block; }
.lp-denoms { margin-top: 14px; border-top: 1px dashed var(--line); padding-top: 14px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.lp-denom { background: var(--cream); border: 1px solid var(--line); border-radius: 12px; padding: 9px 10px; }
.lp-denom b { display: block; font-size: 14px; font-weight: 700; color: var(--ink); }
.lp-denom span { font-size: 11px; color: var(--muted); }
.lp-floaty { position: absolute; background: #fff; border-radius: 15px; box-shadow: var(--shadow-md); border: 1px solid var(--line); padding: 12px 15px; display: flex; align-items: center; gap: 11px; }
.lp-floaty .lp-fi { width: 32px; height: 32px; border-radius: 10px; display: grid; place-items: center; color: #1c1500; background: linear-gradient(150deg, var(--yellow-hi), var(--yellow-deep)); flex: 0 0 auto; }
.lp-floaty b { font-size: 13px; font-weight: 700; color: var(--ink); display: block; }
.lp-floaty span { font-size: 11px; color: var(--muted); }
.lp-floaty-1 { top: -20px; left: -24px; animation: lp-float 6s ease-in-out infinite; }
.lp-floaty-2 { bottom: -18px; right: -18px; animation: lp-float 8s ease-in-out infinite .5s; }

/* ---------- Sections ---------- */
.lp-section { padding: 90px 0; }
.lp-section.cream { background: var(--cream); }
.lp-kicker { text-transform: uppercase; letter-spacing: .15em; font-size: 12.5px; font-weight: 700; color: var(--amber); }
.lp-h2 { font-size: clamp(28px, 3.5vw, 40px); font-weight: 800; letter-spacing: -0.025em; line-height: 1.1; margin-top: 12px; color: var(--ink); }
.lp-lead { font-size: 17px; color: var(--muted); margin-top: 15px; max-width: 640px; }
.lp-head-center { text-align: center; max-width: 720px; margin: 0 auto 52px; }
.lp-head-center .lp-lead { margin-left: auto; margin-right: auto; }

/* ---------- Stats ---------- */
.lp-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.lp-stat { text-align: center; padding: 26px 16px; border-radius: var(--radius); background: var(--paper); border: 1px solid var(--line); box-shadow: var(--shadow-sm); }
.lp-stat b { display: block; font-size: clamp(30px, 4vw, 44px); font-weight: 800; letter-spacing: -0.03em; color: var(--ink); }
.lp-stat b em { font-style: normal; color: var(--yellow-deep); }
.lp-stat span { display: block; margin-top: 6px; font-size: 14px; color: var(--muted); font-weight: 500; }

/* ---------- Features ---------- */
.lp-features { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }
.lp-feature { background: var(--paper); border: 1px solid var(--line); border-radius: var(--radius); padding: 28px; box-shadow: var(--shadow-sm); transition: transform .2s ease, box-shadow .2s ease, border-color .2s; }
.lp-feature:hover { transform: translateY(-5px); box-shadow: var(--shadow-md); border-color: #f4e2a6; }
.lp-feat-icon { width: 50px; height: 50px; border-radius: 15px; display: grid; place-items: center; margin-bottom: 18px; color: #1c1500; background: linear-gradient(150deg, var(--yellow-hi), var(--yellow-deep)); box-shadow: 0 8px 16px rgba(255,183,3,.25); }
.lp-feature h3 { font-size: 18px; font-weight: 700; letter-spacing: -0.01em; color: var(--ink); }
.lp-feature p { margin-top: 9px; font-size: 14.5px; color: var(--muted); line-height: 1.6; }

/* ---------- Workflow ---------- */
.lp-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
.lp-step { position: relative; padding: 32px 26px; border-radius: var(--radius); background: var(--paper); border: 1px solid var(--line); box-shadow: var(--shadow-sm); }
.lp-step-n { width: 46px; height: 46px; border-radius: 14px; display: grid; place-items: center; font-weight: 800; font-size: 18px; color: #1c1500; background: linear-gradient(150deg, var(--yellow-hi), var(--yellow-deep)); margin-bottom: 18px; }
.lp-step h3 { font-size: 19px; font-weight: 700; color: var(--ink); }
.lp-step p { margin-top: 10px; font-size: 14.5px; color: var(--muted); line-height: 1.6; }

/* ---------- Trust ---------- */
.lp-trust { background: var(--cream-2); border: 1px solid #f6e6b0; border-radius: 28px; padding: 56px; }
.lp-trust-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 44px; align-items: center; }
.lp-trust-list { display: grid; gap: 13px; }
.lp-trust-item { display: flex; align-items: flex-start; gap: 13px; padding: 15px 17px; border-radius: 15px; background: rgba(255,255,255,.7); border: 1px solid #f3e4ae; }
.lp-trust-item .lp-ti { flex: 0 0 auto; width: 34px; height: 34px; border-radius: 10px; display: grid; place-items: center; color: #1c1500; background: linear-gradient(150deg, var(--yellow-hi), var(--yellow-deep)); }
.lp-trust-item b { font-size: 15px; font-weight: 700; color: var(--ink); }
.lp-trust-item span { font-size: 13.5px; color: #7b7053; }

/* ---------- Reports ---------- */
.lp-reports { display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; }
.lp-report { display: inline-flex; align-items: center; gap: 9px; font-size: 14.5px; font-weight: 600; color: var(--ink); background: var(--paper); border: 1px solid var(--line); padding: 12px 18px; border-radius: 999px; box-shadow: var(--shadow-sm); transition: transform .15s, border-color .15s; }
.lp-report:hover { transform: translateY(-3px); border-color: var(--yellow-deep); }
.lp-report i { width: 9px; height: 9px; border-radius: 50%; background: var(--yellow-deep); display: block; }

/* ---------- CTA ---------- */
.lp-cta-band { text-align: center; background: linear-gradient(155deg, var(--yellow-hi), var(--yellow-deep)); border-radius: 30px; padding: 66px 40px; position: relative; overflow: hidden; }
.lp-cta-band::before { content: ""; position: absolute; inset: 0; background: radial-gradient(420px 260px at 50% -20%, rgba(255,255,255,.5), transparent 60%); pointer-events: none; }
.lp-cta-band h2 { position: relative; color: #1c1500; font-size: clamp(28px, 3.6vw, 40px); font-weight: 800; letter-spacing: -0.025em; }
.lp-cta-band p { position: relative; color: #6b5300; margin-top: 13px; font-size: 17px; font-weight: 500; }
.lp-cta-actions { position: relative; margin-top: 30px; display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }

/* ---------- Footer ---------- */
.lp-footer { background: var(--cream); border-top: 1px solid var(--line); padding: 54px 0 32px; }
.lp-footer-grid { display: grid; grid-template-columns: 1.6fr 1fr 1fr; gap: 40px; padding-bottom: 32px; border-bottom: 1px solid var(--line); }
.lp-footer-logo { height: 42px; width: auto; margin-bottom: 14px; }
.lp-footer-tag { font-size: 14px; color: var(--muted); max-width: 320px; }
.lp-footer h4 { font-size: 13px; text-transform: uppercase; letter-spacing: .1em; color: #6a6250; margin-bottom: 15px; font-weight: 700; }
.lp-footer-col a { display: block; font-size: 14px; color: var(--muted); padding: 6px 0; transition: color .15s; }
.lp-footer-col a:hover { color: var(--ink); }
.lp-footer-bottom { padding-top: 22px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; font-size: 13px; color: #8b8267; }

/* ---------- Reveal ---------- */
.lp-reveal { opacity: 0; transform: translateY(24px); transition: opacity .7s cubic-bezier(.2,.7,.2,1), transform .7s cubic-bezier(.2,.7,.2,1); }
.lp-reveal.lp-in { opacity: 1; transform: none; }

@media (max-width: 940px) {
  .lp-hero-grid { grid-template-columns: 1fr; gap: 46px; }
  .lp-mock { max-width: 460px; margin: 0 auto; }
  .lp-trust-grid { grid-template-columns: 1fr; gap: 28px; }
  .lp-features { grid-template-columns: repeat(2, 1fr); }
  .lp-footer-grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 720px) {
  .lp-nav-links { display: none; }
  .lp-nav-cta .lp-signin { display: none; }
  .lp-section { padding: 64px 0; }
  .lp-trust { padding: 38px 24px; }
  .lp-stats { grid-template-columns: repeat(2, 1fr); }
  .lp-features { grid-template-columns: 1fr; }
  .lp-steps { grid-template-columns: 1fr; }
  .lp-footer-grid { grid-template-columns: 1fr; gap: 26px; }
  .lp-floaty-1 { left: -4px; }
  .lp-floaty-2 { right: -4px; }
}
`;

const I = {
  tick: (<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>),
  wallet: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7a2 2 0 0 1 2-2h13a1 1 0 0 1 1 1v2"/><path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-8a1 1 0 0 0-1-1H5a2 2 0 0 1-2-2Z"/><circle cx="16" cy="13" r="1.4"/></svg>),
  receipt: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3v18l2-1.4 2 1.4 2-1.4 2 1.4 2-1.4 2 1.4V3l-2 1.4L14 3l-2 1.4L10 3 8 4.4 5 3Z"/><path d="M8 8h8M8 12h8M8 16h5"/></svg>),
  drawer: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18M8 15h3"/></svg>),
  lock: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10" width="16" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>),
  ledger: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5a2 2 0 0 1 2-2h12v18H6a2 2 0 0 1-2-2Z"/><path d="M8 3v18M12 8h4M12 12h4"/></svg>),
  chart: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20V4M4 20h16M8 16v-4M12 16V8M16 16v-7"/></svg>),
  shield: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 5 6v5c0 4.5 3 8.3 7 10 4-1.7 7-5.5 7-10V6Z"/><path d="m9 12 2 2 4-4"/></svg>),
  users: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3"/><path d="M3 20a6 6 0 0 1 12 0"/><path d="M16 5.2a3 3 0 0 1 0 5.6M21 20a6 6 0 0 0-4-5.7"/></svg>),
  coin: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v10M9.5 9.5c0-1 1-1.6 2.5-1.6s2.5.7 2.5 1.7-1 1.4-2.5 1.4-2.5.5-2.5 1.5 1 1.7 2.5 1.7 2.5-.6 2.5-1.6"/></svg>),
  arrow: (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>),
};

const FEATURES = [
  { icon: I.wallet, t: "Cash accounts & transactions", d: "Track balances across all your cash accounts and tills, with receipts, payments and transfers between accounts." },
  { icon: I.receipt, t: "Shifts & Z-Reports", d: "Capture the float, cash sales, cash in/out and driver wages per shift, then finalise the actual cash-to-bank collected." },
  { icon: I.drawer, t: "Denomination reconciliation", d: "Count the drawer note-by-note and coin-by-coin. CashPro compares it to the system balance and books the variance for you." },
  { icon: I.lock, t: "Chronological locking", d: "Reconciliations must be finalised earliest-date-first, and posted periods lock — so they can't be edited out of order." },
  { icon: I.ledger, t: "Ledger & detailed reports", d: "A running per-account ledger with opening and closing balances, running totals, and one-click CSV export." },
  { icon: I.chart, t: "Twelve advanced reports", d: "Till variance, C2B variance, cash in/out, cash sales, driver wage, category, and more — filtered by date and account." },
  { icon: I.users, t: "Role-based access", d: "Admin, Manager, Accountant and Staff roles, each scoped to the specific accounts a user is assigned to." },
  { icon: I.shield, t: "Per-organisation isolation", d: "Every account, user and record is isolated to its own organisation, with a full audit log of every change." },
  { icon: I.coin, t: "Multi-currency ready", d: "Set your organisation's currency and see every balance, report and export formatted consistently throughout." },
];

const STEPS = [
  { t: "Record the shift", d: "Staff log the starting float, cash sales, cash in/out lines, driver wages and the declared cash for each till session." },
  { t: "Reconcile the drawer", d: "Count the physical cash by denomination. CashPro compares it to the system balance and posts any surplus or deficit automatically." },
  { t: "Report & bank", d: "Finalise the cash-to-bank collection, lock the period, and pull the ledger and reports your accountant needs." },
];

const TRUST = [
  { t: "JWT authentication", d: "Signed, expiring session tokens on every request." },
  { t: "Role-based permissions", d: "Access scoped to a user's assigned accounts." },
  { t: "Per-organisation isolation", d: "One tenant can never see another's data." },
  { t: "Audit trail", d: "Every create, update and finalise, kept for 180 days." },
  { t: "Private file storage", d: "Receipt attachments are access-controlled." },
];

const REPORTS = ["Till Variance", "C2B Variance", "Cash In", "Cash Out", "Cash Sales", "Driver Wage", "Category", "Issue Notes", "Reconciliation", "Expected C2B", "Target Cash", "Declared Cash"];

export default function LandingPage() {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const isAuthed = typeof window !== "undefined" && !!localStorage.getItem("token");

  useEffect(() => {
    const els = rootRef.current ? rootRef.current.querySelectorAll(".lp-reveal") : [];
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("lp-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("lp-in"); io.unobserve(e.target); } }),
      { threshold: 0.14 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const goAuth = () => navigate("/auth");
  const goPrimary = () => navigate(isAuthed ? "/dashboard" : "/auth");

  return (
    <div className="lp-root" ref={rootRef}>
      <style>{LP_CSS}</style>

      {/* NAV */}
      <nav className="lp-nav">
        <div className="lp-shell lp-nav-inner">
          <img className="lp-logo" src="/olo.png" alt="CashPro" />
          <div className="lp-nav-links">
            <a href="#features">Features</a>
            <a href="#workflow">How it works</a>
            <a href="#trust">Security</a>
            <a href="#reports">Reports</a>
          </div>
          <div className="lp-nav-cta">
            <a className="lp-signin" onClick={goAuth}>Sign in</a>
            <button className="lp-btn lp-btn-primary" onClick={goPrimary}>{isAuthed ? "Go to dashboard" : "Get started"} {I.arrow}</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="lp-hero">
        <div className="lp-shell lp-hero-grid">
          <div>
            <span className="lp-eyebrow"><span className="lp-dot" /> Cash management, made simple.</span>
            <h1 className="lp-h1">Every coin counted.<br />Every drawer <span className="lp-mark-hl">balanced</span>.</h1>
            <p className="lp-sub">CashPro turns daily till shifts, cash-to-bank deposits and denomination-level reconciliations into one simple, tidy system — across every account, branch and currency.</p>
            <div className="lp-hero-cta">
              <button className="lp-btn lp-btn-primary lp-btn-lg" onClick={goPrimary}>{isAuthed ? "Go to dashboard" : "Create your account"} {I.arrow}</button>
              <button className="lp-btn lp-btn-ghost lp-btn-lg" onClick={goAuth}>Sign in</button>
            </div>
            <div className="lp-chips">
              <span className="lp-chip"><span className="lp-tick">{I.tick}</span> Multi-currency</span>
              <span className="lp-chip"><span className="lp-tick">{I.tick}</span> Role-based access</span>
              <span className="lp-chip"><span className="lp-tick">{I.tick}</span> Full audit log</span>
            </div>
          </div>

          <div className="lp-mock">
            <div className="lp-card">
              <div className="lp-card-top">
                <div className="lp-card-acct">
                  <span className="lp-card-icon">{I.wallet}</span>
                  <div><b>Main Till — Front Counter</b><span>Account · GBP</span></div>
                </div>
                <span className="lp-badge">{I.tick} Balanced</span>
              </div>
              <div className="lp-balance">£4,820.00<small>system balance</small></div>
              <div className="lp-bars" aria-hidden="true">
                <i style={{ height: "40%" }} /><i style={{ height: "62%" }} /><i style={{ height: "48%" }} />
                <i style={{ height: "78%" }} /><i style={{ height: "56%" }} /><i style={{ height: "88%" }} />
                <i style={{ height: "66%" }} /><i style={{ height: "72%" }} />
              </div>
              <div className="lp-denoms">
                <div className="lp-denom"><b>£50 × 40</b><span>£2,000.00</span></div>
                <div className="lp-denom"><b>£20 × 96</b><span>£1,920.00</span></div>
                <div className="lp-denom"><b>£10 × 90</b><span>£900.00</span></div>
              </div>
            </div>
            <div className="lp-floaty lp-floaty-1">
              <span className="lp-fi">{I.tick}</span>
              <div><b>Variance £0.00</b><span>counted = expected</span></div>
            </div>
            <div className="lp-floaty lp-floaty-2">
              <span className="lp-fi">{I.receipt}</span>
              <div><b>C2B finalised</b><span>banked &amp; locked</span></div>
            </div>
          </div>
        </div>
      </header>

      {/* STATS */}
      <section className="lp-section">
        <div className="lp-shell">
          <div className="lp-stats">
            {[["12", "Advanced report types"], ["4", "Permission roles"], ["3", "-tier till workflow", true], ["180", "-day audit history", true]].map(([b, s, em], i) => (
              <div className="lp-stat lp-reveal" key={i} style={{ transitionDelay: `${i * 70}ms` }}>
                <b>{b}{em ? <em>{s.split(" ")[0]}</em> : ""}</b>
                <span>{em ? s.replace(/^\S+\s/, "") : s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="lp-section cream" id="features">
        <div className="lp-shell">
          <div className="lp-head-center lp-reveal">
            <div className="lp-kicker">Everything in one place</div>
            <h2 className="lp-h2">The complete cash-handling toolkit</h2>
            <p className="lp-lead">From the moment cash hits the drawer to the moment it's banked and reported — CashPro tracks every step, for every account.</p>
          </div>
          <div className="lp-features">
            {FEATURES.map((f, i) => (
              <div className="lp-feature lp-reveal" key={i} style={{ transitionDelay: `${(i % 3) * 70}ms` }}>
                <div className="lp-feat-icon">{f.icon}</div>
                <h3>{f.t}</h3>
                <p>{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="lp-section" id="workflow">
        <div className="lp-shell">
          <div className="lp-head-center lp-reveal">
            <div className="lp-kicker">How it works</div>
            <h2 className="lp-h2">Three steps, fully reconciled</h2>
            <p className="lp-lead">CashPro mirrors how cash actually moves through a business — so the books match the drawer, every single day.</p>
          </div>
          <div className="lp-steps">
            {STEPS.map((s, i) => (
              <div className="lp-step lp-reveal" key={i} style={{ transitionDelay: `${i * 90}ms` }}>
                <div className="lp-step-n">{i + 1}</div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="lp-section cream" id="trust">
        <div className="lp-shell">
          <div className="lp-trust">
            <div className="lp-trust-grid">
              <div className="lp-reveal">
                <div className="lp-kicker">Built for trust</div>
                <h2 className="lp-h2">Your cash data, kept safe</h2>
                <p className="lp-lead">Financial records need clear boundaries. CashPro isolates every organisation, scopes access by role, and records what happened — so you always know the books are yours alone.</p>
                <div style={{ marginTop: 26 }}>
                  <button className="lp-btn lp-btn-primary lp-btn-lg" onClick={goPrimary}>{isAuthed ? "Go to dashboard" : "Get started"} {I.arrow}</button>
                </div>
              </div>
              <div className="lp-trust-list">
                {TRUST.map((s, i) => (
                  <div className="lp-trust-item lp-reveal" key={i} style={{ transitionDelay: `${i * 60}ms` }}>
                    <span className="lp-ti">{I.tick}</span>
                    <div><b>{s.t}</b><br /><span>{s.d}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REPORTS */}
      <section className="lp-section" id="reports">
        <div className="lp-shell">
          <div className="lp-head-center lp-reveal">
            <div className="lp-kicker">Reporting</div>
            <h2 className="lp-h2">Twelve reports, ready to export</h2>
            <p className="lp-lead">Slice your cash data by date and account, then export to CSV for your accountant or bookkeeping software.</p>
          </div>
          <div className="lp-reports">
            {REPORTS.map((r, i) => (
              <span className="lp-report lp-reveal" key={i} style={{ transitionDelay: `${(i % 6) * 45}ms` }}><i />{r}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="lp-section">
        <div className="lp-shell">
          <div className="lp-cta-band lp-reveal">
            <h2>Ready to balance your books?</h2>
            <p>Set up your organisation and start reconciling in minutes.</p>
            <div className="lp-cta-actions">
              <button className="lp-btn lp-btn-ink lp-btn-lg" onClick={goPrimary}>{isAuthed ? "Go to dashboard" : "Create your account"} {I.arrow}</button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-shell">
          <div className="lp-footer-grid">
            <div>
              <img className="lp-footer-logo" src="/olo.png" alt="CashPro" />
              <p className="lp-footer-tag">The complete cash-handling and reconciliation system for cash-first businesses.</p>
            </div>
            <div className="lp-footer-col">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#workflow">How it works</a>
              <a href="#reports">Reports</a>
              <a href="#trust">Security</a>
            </div>
            <div className="lp-footer-col">
              <h4>Account</h4>
              <a onClick={goAuth}>Sign in</a>
              <a onClick={goPrimary}>{isAuthed ? "Dashboard" : "Create account"}</a>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <span>© {new Date().getFullYear()} CashPro. All rights reserved.</span>
            <span>Cash management, made simple.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
